import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { PortalCard } from '../portal';

type SettingsNoticeTone = 'info' | 'warning' | 'error';

const TONE_STYLES: Record<
  SettingsNoticeTone,
  { border: string; icon: string; glyph: ReactNode }
> = {
  info: {
    border: 'border-seeker-800',
    icon: 'bg-seeker-800 text-seeker-300',
    glyph: <Info />,
  },
  warning: {
    border: 'border-amber-900/40',
    icon: 'bg-amber-900/30 text-amber-400',
    glyph: <TriangleAlert />,
  },
  error: {
    border: 'border-rose-900/40',
    icon: 'bg-rose-900/30 text-rose-400',
    glyph: <CircleAlert />,
  },
};

interface SettingsNoticeProps {
  tone: SettingsNoticeTone;
  title: ReactNode;
  description: ReactNode;
  /** Optional follow-up button; omit when there is nothing to do here. */
  action?: ReactNode;
  className?: string;
}

/** In-page explanation card for permission-denied and empty states. */
export function SettingsNotice({
  tone,
  title,
  description,
  action,
  className,
}: SettingsNoticeProps) {
  const styles = TONE_STYLES[tone];

  return (
    <PortalCard
      className={cn('flex gap-4 p-6 sm:p-8', styles.border, className)}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg [&>svg]:size-4',
          styles.icon,
        )}
      >
        {styles.glyph}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="text-sm font-bold leading-5 text-white">{title}</div>
        <div className="text-sm leading-5 text-seeker-400">{description}</div>
        {action ? (
          <div className="flex flex-wrap items-center gap-3 pt-2">{action}</div>
        ) : null}
      </div>
    </PortalCard>
  );
}
